import { supabase } from '../lib/supabase';

export const documentManagementService = {
  // ============================================================================
  // DOCUMENT UPLOAD
  // ============================================================================

  async uploadDocument(file, employeeId, documentData) {
    try {
      // 1. Generate unique file path
      const timestamp = Date.now();
      const sanitizedFileName = file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${employeeId}/${documentData?.category}/${timestamp}_${sanitizedFileName}`;

      // 2. Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase
        ?.storage
        ?.from('employee-documents')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // 3. Create metadata record
      const { data: metadataRecord, error: metadataError } = await supabase
        ?.from('employee_documents')
        ?.insert({
          employee_id: employeeId,
          document_category: documentData?.category,
          document_name: documentData?.name,
          file_name: file?.name,
          file_size_bytes: file?.size,
          storage_path: filePath,
          mime_type: file?.type,
          document_date: documentData?.date,
          description: documentData?.description,
          tags: documentData?.tags || [],
          uploaded_by: documentData?.uploadedBy,
          metadata: documentData?.metadata || {}
        })
        ?.select()
        ?.single();

      if (metadataError) throw metadataError;

      return {
        id: metadataRecord?.id,
        storagePath: filePath,
        fileName: file?.name,
        fileSize: file?.size
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  // ============================================================================
  // DOCUMENT RETRIEVAL
  // ============================================================================

  async getDocumentUrl(storagePath) {
    try {
      const { data, error } = await supabase
        ?.storage
        ?.from('employee-documents')
        ?.createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) throw error;

      return data?.signedUrl;
    } catch (error) {
      console.error('Error getting document URL:', error);
      throw error;
    }
  },

  async downloadDocument(storagePath, fileName) {
    try {
      const { data, error } = await supabase
        ?.storage
        ?.from('employee-documents')
        ?.download(storagePath);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  },

  // ============================================================================
  // DOCUMENT SEARCH AND FILTERING
  // ============================================================================

  async searchDocuments(filters = {}) {
    try {
      let query = supabase
        ?.from('employee_documents')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            department,
            position
          )
        `);

      // Filter by employee
      if (filters?.employeeId) {
        query = query?.eq('employee_id', filters?.employeeId);
      }

      // Filter by category
      if (filters?.category) {
        query = query?.eq('document_category', filters?.category);
      }

      // Filter by date range
      if (filters?.startDate) {
        query = query?.gte('document_date', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('document_date', filters?.endDate);
      }

      // Filter by department
      if (filters?.department) {
        query = query?.eq('employees.department', filters?.department);
      }

      // Filter by tags
      if (filters?.tags && filters?.tags?.length > 0) {
        query = query?.contains('tags', filters?.tags);
      }

      // Filter by archived status
      if (filters?.includeArchived === false) {
        query = query?.eq('is_archived', false);
      }

      // Search by name
      if (filters?.searchTerm) {
        query = query?.or(`document_name.ilike.%${filters?.searchTerm}%,file_name.ilike.%${filters?.searchTerm}%,description.ilike.%${filters?.searchTerm}%`);
      }

      // Sort
      const sortBy = filters?.sortBy || 'uploaded_at';
      const sortOrder = filters?.sortOrder || 'desc';
      query = query?.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        employeeId: row?.employee_id,
        employeeName: `${row?.employees?.first_name} ${row?.employees?.last_name}`,
        employeeNumber: row?.employees?.employee_number,
        department: row?.employees?.department,
        position: row?.employees?.position,
        category: row?.document_category,
        documentName: row?.document_name,
        fileName: row?.file_name,
        fileSizeBytes: row?.file_size_bytes,
        storagePath: row?.storage_path,
        mimeType: row?.mime_type,
        documentDate: row?.document_date,
        description: row?.description,
        tags: row?.tags || [],
        uploadedBy: row?.uploaded_by,
        uploadedAt: row?.uploaded_at,
        isArchived: row?.is_archived,
        metadata: row?.metadata,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error searching documents:', error);
      throw error;
    }
  },

  async getDocumentById(documentId) {
    try {
      const { data, error } = await supabase
        ?.from('employee_documents')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            department,
            position,
            email
          )
        `)
        ?.eq('id', documentId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        employeeId: data?.employee_id,
        employeeName: `${data?.employees?.first_name} ${data?.employees?.last_name}`,
        employeeNumber: data?.employees?.employee_number,
        employeeEmail: data?.employees?.email,
        department: data?.employees?.department,
        position: data?.employees?.position,
        category: data?.document_category,
        documentName: data?.document_name,
        fileName: data?.file_name,
        fileSizeBytes: data?.file_size_bytes,
        storagePath: data?.storage_path,
        mimeType: data?.mime_type,
        documentDate: data?.document_date,
        description: data?.description,
        tags: data?.tags || [],
        uploadedBy: data?.uploaded_by,
        uploadedAt: data?.uploaded_at,
        isArchived: data?.is_archived,
        metadata: data?.metadata
      };
    } catch (error) {
      console.error('Error fetching document:', error);
      throw error;
    }
  },

  // ============================================================================
  // DOCUMENT MANAGEMENT
  // ============================================================================

  async updateDocument(documentId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('employee_documents')
        ?.update({
          document_name: updates?.documentName,
          description: updates?.description,
          tags: updates?.tags,
          document_date: updates?.documentDate,
          metadata: updates?.metadata
        })
        ?.eq('id', documentId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        documentName: data?.document_name,
        description: data?.description,
        tags: data?.tags,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  },

  async archiveDocument(documentId) {
    try {
      const { data, error } = await supabase
        ?.from('employee_documents')
        ?.update({ is_archived: true })
        ?.eq('id', documentId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        isArchived: data?.is_archived
      };
    } catch (error) {
      console.error('Error archiving document:', error);
      throw error;
    }
  },

  async deleteDocument(documentId) {
    try {
      // 1. Get document metadata
      const document = await this.getDocumentById(documentId);

      // 2. Delete from storage
      const { error: storageError } = await supabase
        ?.storage
        ?.from('employee-documents')
        ?.remove([document?.storagePath]);

      if (storageError) throw storageError;

      // 3. Delete metadata record
      const { error: dbError } = await supabase
        ?.from('employee_documents')
        ?.delete()
        ?.eq('id', documentId);

      if (dbError) throw dbError;

      return { success: true };
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  // ============================================================================
  // STATISTICS AND ANALYTICS
  // ============================================================================

  async getDocumentStatistics(employeeId = null) {
    try {
      let query = supabase
        ?.from('employee_documents')
        ?.select('document_category, file_size_bytes', { count: 'exact' });

      if (employeeId) {
        query = query?.eq('employee_id', employeeId);
      }

      query = query?.eq('is_archived', false);

      const { data, error, count } = await query;

      if (error) throw error;

      // Calculate statistics
      const categoryCount = {};
      let totalSize = 0;

      data?.forEach(doc => {
        categoryCount[doc?.document_category] = (categoryCount?.[doc?.document_category] || 0) + 1;
        totalSize += doc?.file_size_bytes || 0;
      });

      return {
        totalDocuments: count || 0,
        totalSizeBytes: totalSize,
        categoryBreakdown: categoryCount
      };
    } catch (error) {
      console.error('Error fetching document statistics:', error);
      throw error;
    }
  },

  // ============================================================================
  // BULK OPERATIONS
  // ============================================================================

  async bulkArchive(documentIds) {
    try {
      const { data, error } = await supabase
        ?.from('employee_documents')
        ?.update({ is_archived: true })
        ?.in('id', documentIds)
        ?.select();

      if (error) throw error;

      return {
        success: true,
        count: data?.length || 0
      };
    } catch (error) {
      console.error('Error bulk archiving documents:', error);
      throw error;
    }
  },

  async bulkDelete(documentIds) {
    try {
      // 1. Get all document paths
      const { data: documents, error: fetchError } = await supabase
        ?.from('employee_documents')
        ?.select('storage_path')
        ?.in('id', documentIds);

      if (fetchError) throw fetchError;

      const storagePaths = documents?.map(doc => doc?.storage_path) || [];

      // 2. Delete from storage
      if (storagePaths?.length > 0) {
        const { error: storageError } = await supabase
          ?.storage
          ?.from('employee-documents')
          ?.remove(storagePaths);

        if (storageError) throw storageError;
      }

      // 3. Delete metadata records
      const { error: dbError } = await supabase
        ?.from('employee_documents')
        ?.delete()
        ?.in('id', documentIds);

      if (dbError) throw dbError;

      return {
        success: true,
        count: documentIds?.length
      };
    } catch (error) {
      console.error('Error bulk deleting documents:', error);
      throw error;
    }
  }
};

export default documentManagementService;